<script>
import useSharedData from "@/use/SharedData.js";
import useValidation from "@/use/Validation.js";
import useTools from "@/use/Tools.js";
import {computed, onMounted, ref} from "vue";
import useSharedMethod from "@/use/SharedMethod.js";
import {useProcessModelStore} from "@/stores/Boarding/ProcessModel.js";
import useSharedSelectData from "@/use/SharedSelectData";
import StrTaskModelSelect from "@/views/Boarding/TaskModel/Components/StrTaskModelSelect";
import axios from "axios";
import WCodeGeneration from "@/components/WCodeGeneration.vue";

export default {
    components: {WCodeGeneration, StrTaskModelSelect},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },

    setup(props) {
        const {mxRules} = useValidation();
        const {loading, dialog, overlay, itemColorHex} = useSharedData();
        const {boardingCategories, boardingChronologies, boardingExecutions} = useSharedSelectData();
        const {$route, $refs} = useTools();
        const {getBack} = useSharedMethod();

        const processModelStore = useProcessModelStore();


        const readonly = computed(() => {
            return props.viewAction || props.deleteAction;
        });

        const processModel = ref({
            tasks: [],
            occupationIds: [],
        });

        const taskDialog = ref(false);
        const selectedTaskIds = computed(() => processModel.value.tasks.map(e => e.taskModelId));

        const stepper = ref(1);

        function addTask(task) {
            processModel.value.tasks.push({
                taskModelId: task.id,
                taskModelName: task.name,
                valid: true,
            })
        }

        function removeTask(task) {
            processModel.value.tasks = processModel.value.tasks.filter(element => element.taskModelId !== task.id);
        }

        function findById(id) {
            loading.value = true;
            axios.get(`/boardingProcessModels/${id}`).then((response) => {
                processModel.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function updateOrderTask() {
            for (const [index, value] of processModel.value.tasks.entries()) {
                value.order = index;
            }
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                updateOrderTask();
                axios.post('/boardingProcessModels', processModel.value).then((response) => {
                    processModelStore.addProcessModel(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                updateOrderTask();
                axios.put('/boardingProcessModels', processModel.value).then((response) => {
                    processModelStore.updateProcessModel(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/boardingProcessModels/${processModel.value.id}`).then(() => {
                processModelStore.deleteProcessModel(processModel.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            stepper,
            processModel,
            mxRules,
            readonly,
            loading,
            itemColorHex,
            dialog,
            taskDialog,
            overlay,
            boardingCategories,
            boardingChronologies,
            boardingExecutions,
            selectedTaskIds,
            addTask,
            removeTask,
            save,
            update,
            remove,
            getBack,
        };
    },
};
</script>

<template>
    <div>
        <v-dialog v-model="dialog" persistent scrollable max-width="1200">
            <v-card>
                <w-dialog-toolbar
                    :add-action="addAction"
                    :update-action="updateAction"
                    :delete-action="deleteAction"
                    :view-action="viewAction"
                ></w-dialog-toolbar>
                <w-overlay
                    :loading="loading"
                    :overlay="overlay"
                    :delete-action="deleteAction"
                    @remove="remove"
                    @cancel="overlay = false"
                ></w-overlay>
                <v-card-text style="background-color: #FAFAFA;">
                    <v-container fluid>
                        <w-fieldset :title="$t('common.details')">
                            <v-form ref="form" lazy-validation>
                                <v-row dense>
                                    <v-col cols="12" md="4">
                                        <w-label :label="$t('common.code')" required/>
                                        <w-code-generation
                                            v-model="processModel.code"
                                            entity="BOARDING_PROCESS_MODEL"
                                            :load="addAction"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        ></w-code-generation>
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <w-label :label="$t('common.name')" required/>
                                        <w-text-field
                                            v-model="processModel.name"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <w-label :label="$t('boarding.category')" required/>
                                        <w-enum-select
                                            v-model="processModel.category"
                                            :items="boardingCategories"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <!--<v-col cols="12" md="6">
                                        <w-label :label="$t('common.description')"/>
                                        <w-text-area
                                            v-model="processModel.description"
                                            :readonly="readonly"
                                        />
                                    </v-col>-->
                                    <v-col cols="6">
                                        <w-label :label="$t('data_select.SOCIO_PROFESSIONAL')"/>
                                        <w-data-select-autocomplete
                                            v-model="processModel.socioProfessionalIds"
                                            :readonly="readonly"
                                            multiple
                                            type="SOCIO_PROFESSIONAL"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('module.occupations')"/>
                                        <str-occupation-autocomplete
                                            v-model="processModel.occupationIds"
                                            :readonly="readonly"
                                            multiple
                                        />
                                    </v-col>
                                </v-row>
                            </v-form>
                        </w-fieldset>
                        <br/>
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <w-fieldset
                                    :title="$t('module.task_models')"
                                    :show-add-action="!readonly"
                                    @add="taskDialog = true"
                                >
                                    <str-task-model-select
                                        :dialog="taskDialog"
                                        :ids="selectedTaskIds"
                                        @add="addTask"
                                        @remove="removeTask"
                                        @close="taskDialog = false"
                                    />
                                    <v-simple-table dense height="300" fixed-header>
                                        <template v-slot:default>
                                            <thead>
                                            <tr>
                                                <th class="text-left">{{ $t('common.name') }}</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr
                                                v-for="(item, index) in processModel.tasks"
                                                :key="`ptask-${index}`"
                                                :style="`background-color: ${stepper === index + 1 ? itemColorHex: ''};`"
                                                @click="stepper = index + 1"
                                            >
                                                <td>{{ item.taskModelName }}</td>
                                            </tr>
                                            </tbody>
                                        </template>
                                    </v-simple-table>
                                </w-fieldset>
                            </v-col>
                            <v-col cols="12" md="6">
                                <w-fieldset :title="$t('common.params')">
                                    <v-stepper
                                        v-model="stepper"
                                        flat
                                        v-if="processModel.tasks.length > 0"
                                    >
                                        <v-stepper-items>
                                            <v-stepper-content
                                                v-for="(task, index) in processModel.tasks"
                                                :key="`step-content-${index}`"
                                                :step="index + 1"
                                            >
                                                <v-form :ref="`form${index}`" lazy-validation>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <w-label :label="$t('boarding.chronology')" required/>
                                                            <w-enum-select
                                                                v-model="task.chronology"
                                                                :items="boardingChronologies"
                                                                :readonly="readonly"
                                                                :rules="[mxRules.required]"
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <w-label :label="$t('boarding.execution')" required/>
                                                            <w-enum-select
                                                                v-model="task.execution"
                                                                :items="boardingExecutions"
                                                                :readonly="readonly"
                                                                :rules="[mxRules.required]"
                                                            />
                                                        </v-col>
                                                        <v-col cols="12" v-if="task.execution === 'ORGANIZATIONAL_CHART'">
                                                            <w-label :label="$t('boarding.ORGANIZATIONAL_CHART')" required/>
                                                            <organizational-chart-field
                                                                v-model="task.organizationalChartId"
                                                                :readonly="readonly"
                                                                :rules="[mxRules.required]"
                                                            />
                                                        </v-col>
                                                        <v-col cols="12" v-if="task.execution === 'SPECIFIC'">
                                                            <w-label :label="$t('module.worker')" required/>
                                                            <str-worker-autocomplete
                                                                v-model="task.workerId"
                                                                :readonly="readonly"
                                                                :rules="[mxRules.required]"
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <w-label :label="$t('boarding.instruction')"/>
                                                            <w-text-area
                                                                v-model="task.instruction"
                                                                :readonly="readonly"
                                                            ></w-text-area>
                                                        </v-col>
                                                    </v-row>
                                                </v-form>
                                            </v-stepper-content>
                                        </v-stepper-items>
                                    </v-stepper>
                                </w-fieldset>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="getBack"/>
                    <w-btn-add
                        :show="addAction"
                        :loading="loading"
                        @press="save(false)"
                    />
                    <w-btn-update
                        :show="updateAction"
                        :loading="loading"
                        @press="update"
                    />
                    <w-btn-delete
                        :show="deleteAction"
                        :loading="loading"
                        @press="overlay = true"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>